import { FileSchema } from './common';
import { repeat, unreachable } from '../util';
import { number, z, ZodSchema, type ZodErrorMap } from 'zod';
import type { IGameState, IStage } from '.';

export const ZintuigenDataSchema = z.object({}).default({});

export type Stage = IStage | 'play-rounds';

export const ZintuigenEmotions = [
	'wow',
	'blij-verrast',
	'gaaf',
	'aangenaam',
	'relaxed',
	'kalm',
	'teleurstellend',
	'stress',
	'irritatie',
] as const;
export type ZintuigenEmotion = (typeof ZintuigenEmotions)[number];
export const ZintuigenMedals = ['Goud', 'Zilver', 'Brons'] as const;
export type ZintuigenMedal = (typeof ZintuigenMedals)[number];

export function medalForEmotions(emotion: ZintuigenEmotion): ZintuigenMedal {
	// prettier-ignore
	switch (emotion) {
		case 'wow': return 'Goud';
		case 'blij-verrast': return 'Goud'
		case 'gaaf': return 'Goud'
		case 'aangenaam': return 'Zilver'
		case 'relaxed': return 'Zilver'
		case 'kalm': return 'Zilver'
		case 'teleurstellend': return 'Brons'
		case 'stress': return 'Brons'
		case 'irritatie': return 'Brons'
		default: unreachable(emotion);
	}
}

export const ZintuigenRoundSchema = z.object({
	topic: z.string(),
	correctAnswer: z.enum(ZintuigenEmotions),
	roundImage: FileSchema.optional(),
	photo1: FileSchema.optional(),
	photo2: FileSchema.optional(),
	photo3: FileSchema.optional(),
});
export type ZintuigenRound = z.infer<typeof ZintuigenRoundSchema>;

export const ZintuigenPresetSchema = z.object({
	presetName: z.string().default('No name'),
	rounds: z.array(ZintuigenRoundSchema).default([]),
});
export type ZintuigenPreset = z.infer<typeof ZintuigenPresetSchema>;

export type ZintuigenPlayerState = {
	id: string;
	name: string;
	color: string;
	score: number;
	lastScreen: ZintuigenUpdate;
	roundAnswers: { emotion?: ZintuigenEmotion }[];
	roundTips: { tip?: string }[];
};

export type ZintuigenState = {
	stage: Stage;
	roundIndex: number;
	showPreface: boolean;
	players: ZintuigenPlayerState[];
	game: ZintuigenPreset;
};

export const ZintuigenUpdateSchema = z.discriminatedUnion('kind', [
	z.object({ kind: z.literal('trigger-effect'), effect: z.enum(['confetti', 'red-flash']), text: z.string().optional() }),
	z.object({ kind: z.literal('show-login-screen'), failed: z.boolean().optional() }),
	z.object({ kind: z.literal('show-register-screen') }),
	z.object({
		kind: z.literal('show-story-screen'),
		title: z.string(),
		round: z.number(),
		message: z.string(),
		image: z.string(),
	}),
	z.object({
		kind: z.literal('show-question-screen'),
		topic: z.string(),
		round: z.number(),

		timerFrom: z.number(),
		timerTo: z.number(),
	}),
	z.object({
		kind: z.literal('show-enter-tip-screen'),
		topic: z.string(),
		round: z.number(),

		timerFrom: z.number(),
		timerTo: z.number(),
	}),
	z.object({
		kind: z.literal('show-answer-wait-screen'),
		topic: z.string(),
		round: z.number(),
		yourAnswer: z.enum(ZintuigenEmotions).optional(),
	}),
	z.object({
		kind: z.literal('show-enter-tip-wait-screen'),
		topic: z.string(),
		round: z.number(),
		yourAnswer: z.enum(ZintuigenEmotions).optional(),
	}),

	z.object({
		kind: z.literal('show-answer-screen'),
		topic: z.string(),
		round: z.number(),
		yourAnswer: z.enum(ZintuigenEmotions).optional(),
		correctAnswer: z.enum(ZintuigenEmotions),
		photos: z.array(z.string()),
	}),
	z.object({
		kind: z.literal('show-tip-screen'),
		topic: z.string(),
		round: z.number(),
		goldTipPlayerName: z.string(),
		goldTipPlayerColor: z.string(),
		silverTipPlayerName: z.string(),
		silverTipPlayerColor: z.string(),
	}),
	z.object({
		kind: z.literal('show-results-screen'),
		players: z.array(z.object({ name: z.string(), color: z.string(), score: z.number() })),
	}),

	z.object({
		kind: z.literal('update-players-state'),
		players: z.array(z.object({ name: z.string(), color: z.string() })),
	}),
	z.object({
		kind: z.literal('update-player-state'),
		player: z.object({
			//
			name: z.string(),
			color: z.string(),
			score: z.number(),
		}),
	}),
]);
export type ZintuigenUpdate = z.infer<typeof ZintuigenUpdateSchema>;

export const ZintuigenMessageSchema = z.discriminatedUnion('kind', [
	// TODO: make it so this can only be sent by admin
	z.object({ kind: z.literal('start-game') }),
	z.object({ kind: z.literal('end-preface') }),
	z.object({ kind: z.literal('end-question') }),
	z.object({ kind: z.literal('force-restart-round') }),
	z.object({ kind: z.literal('show-enter-tip-screen') }),
	z.object({ kind: z.literal('set-player-active'), playerId: z.string(), active: z.boolean() }),
	z.object({ kind: z.literal('show-question-results') }),
	z.object({ kind: z.literal('show-enter-tip') }),
	z.object({ kind: z.literal('show-tip-results'), goldenTipPlayerId: z.string().optional(), silverTipPlayerId: z.string().optional() }),
	z.object({ kind: z.literal('start-next-round') }),

	z.object({ kind: z.literal('update-player-data'), playerId: z.string(), name: z.string(), color: z.string() }),
	z.object({ kind: z.literal('remove-player-data'), playerId: z.string() }),
	z.object({ kind: z.literal('submit-answer'), playerId: z.string(), emotion: z.enum(ZintuigenEmotions) }),
	z.object({ kind: z.literal('submit-tip'), playerId: z.string(), tip: z.string() }),
]);

export type ZintuigenMessage = z.infer<typeof ZintuigenMessageSchema>;
